/**
 * LearnMoreLink — the single in-app entry point into the owned Help Center
 * (eleplan.com/help, backed by the `help_articles` table — see ED-2559).
 *
 * Every feature surface that has a help article links to it through this
 * component, so the article a screen points at is one grep away and the
 * next move of the Help Center is a one-line change in `helpLinks.ts`.
 *
 *   <LearnMoreLink slug="plan-vault" />
 *   <LearnMoreLink slug="secure-sharing" label="How sharing works" />
 *   <LearnMoreLink slug="benefits-screening" variant="pill" align="center" />
 *
 * Two shapes:
 *   - `inline` (default) — a small primary-coloured text link with a trailing
 *     external-link glyph. Sits under body copy in sheets and screens.
 *   - `pill` — a bordered chip for empty states and intro-sheet footers where
 *     an untinted text link would disappear.
 */

import React, { useCallback } from 'react';
import { Linking, StyleSheet, Text, TouchableOpacity, ViewStyle } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { helpArticleUrl } from '../utilities/helpLinks';
import Icon from './Icon';

interface Props {
  /** Help-article slug, e.g. 'plan-vault'. Must exist in `help_articles`. */
  slug: string;
  /** Link text. Defaults to "Learn more". */
  label?: string;
  variant?: 'inline' | 'pill';
  align?: 'left' | 'center';
  style?: ViewStyle | ViewStyle[];
}

export default function LearnMoreLink({
  slug,
  label = 'Learn more',
  variant = 'inline',
  align = 'left',
  style,
}: Props) {
  const theme = useTheme();

  const open = useCallback(() => {
    // A dead help link should never surface an error to the user — the Help
    // Center is supplementary to every screen it appears on.
    Linking.openURL(helpArticleUrl(slug)).catch(() => {});
  }, [slug]);

  const isPill = variant === 'pill';

  return (
    <TouchableOpacity
      onPress={open}
      activeOpacity={0.7}
      accessibilityRole="link"
      accessibilityLabel={`${label} — opens the Eleplan Help Center`}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      style={[
        styles.base,
        align === 'center' && styles.center,
        isPill && [
          styles.pill,
          { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
        ],
        style as ViewStyle,
      ]}
    >
      <Text style={[styles.label, { color: theme.colors.primary }]}>{label}</Text>
      <Icon icon={['fas', 'arrow-up-right-from-square']} size={10} color={theme.colors.primary} />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    paddingVertical: 6,
  },
  center: {
    alignSelf: 'center',
    justifyContent: 'center',
  },
  pill: {
    alignSelf: 'center',
    paddingVertical: 9,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
  },
});
